import React from 'react';

import { Flex } from '@chakra-ui/react';
import { Form } from 'react-final-form';

import {
  formValidator,
  useErrorTranslator,
} from 'modules/Inquiry/inquiryFieldValidation/useErrorTranslations';
import { useFetchInquiryData } from 'modules/InquiryDetails/FetchInquiryDetails';
import { translations } from 'new/form/common/types';
import { withAssociatedPeopleEditMode } from 'pages/operationPortal/CompaniesDetails/AssiciatedPerson/components/associatedPeopleMode/withAssociatedPeopleEditMode';
import { BeneficialOwner } from 'pages/operationPortal/CompaniesDetails/AssiciatedPerson/components/beneficialOwners/fields';
import { usePatchDzbBeneficialOwner } from 'pages/operationPortal/CompaniesDetails/AssiciatedPerson/hooks/beneficialOwner/patchBeneficialOwners';
import ButtonComponent from 'theme/components/Button';
import { ModalComponent, ModalProps } from 'theme/components/Modal';
import { useTranslations } from 'utils/hooks/useTranslations';

import BeneficialOwnerFields from './BeneficialOwnerFields';
import { beneficialOwnerSchema } from './beneficialOwnerSchema';

const EDIT_BENEFICIAL_OWNER_FORM_ID = 'editBeneficialOwnerForm';

type Props = Pick<ModalProps, 'isOpen' | 'onClose'> & {
  initialValues: BeneficialOwner | undefined;
};

const EditBeneficialOwnerModal = ({ isOpen, onClose, initialValues }: Props) => {
  const { mutateAsync, isLoading } = usePatchDzbBeneficialOwner();
  const { refetchInquiry } = useFetchInquiryData();

  const errorTranslator = useErrorTranslator();
  const t = useTranslations();
  const { actions } = translations.pages.companiesDetails.associatedPerson.new.beneficialOwners;

  const onSubmit = async (values: BeneficialOwner) => {
    await mutateAsync(values);
    await refetchInquiry();
    onClose();
  };

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      title={t(actions.edit.title)}
      footer={
        <Flex gap={4}>
          <ButtonComponent variant={'tertiary'} onClick={onClose} testId="cancelButton">
            {t(actions.edit.cancel)}
          </ButtonComponent>
          <ButtonComponent
            isLoading={isLoading}
            variant={'primary'}
            form={EDIT_BENEFICIAL_OWNER_FORM_ID}
            type={'submit'}
            testId="confirmButton"
          >
            {t(actions.edit.confirm)}
          </ButtonComponent>
        </Flex>
      }
    >
      <Form
        onSubmit={onSubmit}
        initialValues={initialValues}
        validate={(values) => formValidator(values, beneficialOwnerSchema, errorTranslator)}
        render={({ handleSubmit }) => {
          return (
            <form onSubmit={handleSubmit} id={EDIT_BENEFICIAL_OWNER_FORM_ID}>
              <BeneficialOwnerFields mode={'edit'} />
            </form>
          );
        }}
      />
    </ModalComponent>
  );
};

export default withAssociatedPeopleEditMode(EditBeneficialOwnerModal);
